import catalogLogo from "../../assets/logo.svg";
import { splitIntoColumns, CATALOG_COLUMN_COUNT } from "../utils/catalogColumns";
import { resolveGroupThemeKey } from "../utils/catalogThemes";

/** A4 at 96dpi — fixed so layout never depends on monitor size. */
const PAGE_WIDTH_PX = 794;
const PAGE_HEIGHT_PX = 1123;
const CONTENT_WIDTH_PX = 766;

const GROUP_THEME_PALETTE = [
  { key: "sky", headerBg: "#edf5ff", headerBorder: "#cfe1ff", headerText: "#2563eb" },
  { key: "mint", headerBg: "#ecfbf3", headerBorder: "#c9f0dd", headerText: "#059669" },
  { key: "amber", headerBg: "#fff7ea", headerBorder: "#f7dfb1", headerText: "#d97706" },
  { key: "rose", headerBg: "#fff0f4", headerBorder: "#f7c8d6", headerText: "#e11d48" },
  { key: "violet", headerBg: "#f4efff", headerBorder: "#d9cdfd", headerText: "#7c3aed" },
  { key: "teal", headerBg: "#ecfbfc", headerBorder: "#bce9eb", headerText: "#0f766e" },
];

const getGroupTheme = (group) => {
  const themeKey = resolveGroupThemeKey(group);
  return (
    GROUP_THEME_PALETTE.find((theme) => theme.key === themeKey) || GROUP_THEME_PALETTE[0]
  );
};

/**
 * Continuous CSS multi-column print — fills each A4 page densely.
 * Groups are emitted board column-major (col1 → col2 → col3).
 * Break inside a group is ALLOWED so tall groups continue on the next page
 * (header repeats). Only a single product row avoids mid-line splits.
 */
const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 8mm 4mm;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0 auto;
    padding: 0;
    width: ${PAGE_WIDTH_PX}px;
    min-width: ${PAGE_WIDTH_PX}px;
    max-width: ${PAGE_WIDTH_PX}px;
    height: auto;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: 12px;
    background: #fff;
    color: #57606a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pricing-catalog-print-root {
    width: ${CONTENT_WIDTH_PX}px;
    max-width: ${CONTENT_WIDTH_PX}px;
    margin: 0 auto;
    padding: 0;
  }
  .print-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 3mm;
    margin-bottom: 3mm;
    padding: 0 1mm;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .print-header .left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2mm;
  }
  .print-title {
    font-size: 14px;
    font-weight: 600;
    color: #424a53;
  }
  .print-contacts {
    display: flex;
    flex-direction: column;
    gap: 1.5mm;
    align-items: flex-start;
  }
  .print-contact {
    position: relative;
    padding-left: 3mm;
    font-size: 12px;
    color: #000;
  }
  .print-contact::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1mm;
    height: 4mm;
    background: #2f80ed;
    border-radius: 0.5mm;
  }
  .print-meta {
    margin-top: 1.5mm;
    font-size: 11px;
    color: #6e7781;
    text-align: right;
  }
  .catalog-print-flow {
    width: 100%;
    column-count: ${CATALOG_COLUMN_COUNT};
    column-gap: 2.5mm;
    column-fill: auto;
    column-width: auto;
  }
  .group-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    margin: 0 0 2.5mm;
    border: 1px solid #dce3eb;
    border-radius: 2mm;
    background: #fff;
    /* ALLOW split across pages / columns — no blank jump for tall groups. */
    break-inside: auto;
    page-break-inside: auto;
    -webkit-column-break-inside: auto;
    break-before: auto;
    break-after: auto;
    page-break-before: auto;
    page-break-after: auto;
    display: table;
  }
  /* Repeat group title when the same group continues on the next page. */
  .group-table thead {
    display: table-header-group;
  }
  .catalog-print-group-header {
    padding: 2.5mm 2mm;
    font-weight: 700;
    font-size: 13px;
    line-height: 1.2;
    border-bottom: 1px solid #dce3eb;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
  }
  /* Keep one product line intact; do not block the whole group. */
  .group-table tr.product-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .group-table td {
    padding: 1mm;
    border-bottom: 1px solid #e9eef2;
    font-size: 12px;
    color: #57606a;
    vertical-align: middle;
  }
  .group-table tr.product-row:last-child td {
    border-bottom: 0;
  }
  .group-table td.product-name {
    width: 58%;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.25;
  }
  .group-table td.variant {
    width: 22%;
    white-space: nowrap;
    text-align: right;
    font-size: 11px;
    color: #6e7781;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .group-table td.price {
    width: 20%;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }
  .group-table-empty td {
    text-align: center;
    color: #8b95a3;
    font-size: 13px;
    padding: 4mm 3mm;
  }
  .catalog-print-empty {
    text-align: center;
    color: #8b95a3;
    padding: 20mm 0;
    font-size: 14px;
  }
  @media print {
    html, body {
      width: ${PAGE_WIDTH_PX}px;
      min-width: ${PAGE_WIDTH_PX}px;
      max-width: ${PAGE_WIDTH_PX}px;
      height: auto;
    }
    .pricing-catalog-print-root {
      width: ${CONTENT_WIDTH_PX}px;
      max-width: ${CONTENT_WIDTH_PX}px;
    }
    .catalog-print-flow {
      column-count: ${CATALOG_COLUMN_COUNT};
      column-gap: 2.5mm;
      column-fill: auto;
    }
    .group-table {
      break-inside: auto !important;
      page-break-inside: auto !important;
      -webkit-column-break-inside: auto !important;
      border-radius: 0;
      display: table !important;
    }
    .group-table thead {
      display: table-header-group;
    }
  }
`;

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const fmtPrice = (symbol, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  const sym = escapeHtml(symbol || "€");
  return `${sym}${Math.round(num)}`;
};

const resolveLogoUrl = () => {
  if (!catalogLogo) return "";
  if (String(catalogLogo).startsWith("http")) return catalogLogo;
  return `${window.location.origin}${catalogLogo}`;
};

const normalizeColumnStacks = (groupColumns = []) => {
  const isColumnStacks =
    Array.isArray(groupColumns) &&
    groupColumns.length > 0 &&
    groupColumns.every((col) => Array.isArray(col));

  const stacks = isColumnStacks
    ? groupColumns.map((col) => (col || []).filter(Boolean))
    : splitIntoColumns(groupColumns).map((column) =>
        column.map(({ item }) => item).filter(Boolean)
      );

  while (stacks.length < CATALOG_COLUMN_COUNT) {
    stacks.push([]);
  }

  return stacks.slice(0, CATALOG_COLUMN_COUNT);
};

/** Flatten board columns (col1 → col2 → col3) for continuous print flow. */
const flattenGroupsForPrint = (columnStacks = []) => {
  const groups = [];
  columnStacks.forEach((col) => {
    (col || []).forEach((group) => {
      if (!group) return;
      groups.push({
        ...group,
        products: [...(group.products || [])],
      });
    });
  });
  return groups;
};

const renderGroup = (group) => {
  const theme = getGroupTheme(group);
  const products = group.products || [];
  const rows = products.length
    ? products
        .map(
          (product) => `
        <tr class="product-row">
          <td class="product-name">${escapeHtml(product.name || "")}</td>
          <td class="variant">${escapeHtml(product.variant || "")}</td>
          <td class="price">${fmtPrice(group.currencySymbol, product.price)}</td>
        </tr>`
        )
        .join("")
    : `<tr class="group-table-empty"><td colspan="3">No products in this group</td></tr>`;

  return `
      <table class="group-table">
        <colgroup>
          <col style="width:58%" />
          <col style="width:22%" />
          <col style="width:20%" />
        </colgroup>
        <thead>
          <tr>
            <th
              colspan="3"
              class="catalog-print-group-header"
              style="background:${theme.headerBg};border-color:${theme.headerBorder};color:${theme.headerText};"
            >
              ${escapeHtml(group.name || "Group")}
            </th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
};

const renderPrintHeader = (logoSvg, printDate) => `
        <div class="print-header">
          <div class="left">
            ${logoSvg}
            <div class="print-title">Mobile Pricing Catalog</div>
          </div>
          <div>
            <div class="print-contacts">
              <div class="print-contact">+49 1577 777 3887 (WhatsApp available)</div>
              <div class="print-contact">Lärchenstraße 78, 65933 Frankfurt am Main</div>
              <div class="print-contact">info@mrphone.gmbh</div>
            </div>
            <div class="print-meta">${escapeHtml(printDate)}</div>
          </div>
        </div>`;

/** Renders isolated print HTML — continuous 3-column flow, groups may span pages. */
export function renderPrintHtml(groupColumns = []) {
  const columnStacks = normalizeColumnStacks(groupColumns);
  const groups = flattenGroupsForPrint(columnStacks);
  const logoUrl = resolveLogoUrl();
  const logoSvg = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="Mr.Phone" style="height: 9mm; width: auto; max-width: 52mm; object-fit: contain;" />`
    : "";

  const printDate = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const bodyHtml = !groups.length
    ? `<p class="catalog-print-empty">No product groups to export.</p>`
    : `${renderPrintHeader(logoSvg, printDate)}
      <div class="catalog-print-flow">
        ${groups.map((group) => renderGroup(group)).join("\n")}
      </div>`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=${PAGE_WIDTH_PX}, initial-scale=1, maximum-scale=1, user-scalable=no" />
    <title>Print - Pricing Catalog</title>
    <style>${PRINT_STYLES}</style>
  </head>
  <body>
    <div class="pricing-catalog-print-root">
      ${bodyHtml}
    </div>
  </body>
</html>`;
}

const lockPrintDocumentLayout = (doc, iframe) => {
  const pageWidth = `${PAGE_WIDTH_PX}px`;
  const contentWidth = `${CONTENT_WIDTH_PX}px`;

  [doc.documentElement, doc.body].forEach((el) => {
    if (!el) return;
    el.style.width = pageWidth;
    el.style.minWidth = pageWidth;
    el.style.maxWidth = pageWidth;
    el.style.margin = "0 auto";
    el.style.height = "auto";
  });

  const root = doc.querySelector(".pricing-catalog-print-root");
  if (root) {
    root.style.width = contentWidth;
    root.style.maxWidth = contentWidth;
  }

  const flow = doc.querySelector(".catalog-print-flow");
  if (flow) {
    flow.style.columnCount = String(CATALOG_COLUMN_COUNT);
    flow.style.columnGap = "2.5mm";
    flow.style.columnFill = "auto";
    flow.style.width = "100%";
  }

  // Let iframe grow with content so print is not clipped to one frame.
  if (iframe) {
    const contentHeight = Math.max(
      PAGE_HEIGHT_PX,
      doc.documentElement?.scrollHeight || 0,
      doc.body?.scrollHeight || 0
    );
    iframe.style.height = `${contentHeight}px`;
  }
};

/** Print via hidden iframe at fixed A4 width — height grows with content. */
export function printPricingCatalog(groupColumns = []) {
  return new Promise((resolve, reject) => {
    try {
      const html = renderPrintHtml(groupColumns);
      const iframe = document.createElement("iframe");
      iframe.setAttribute("title", "Pricing catalog print");
      iframe.setAttribute(
        "style",
        [
          "position:fixed",
          "left:-10000px",
          "top:0",
          `width:${PAGE_WIDTH_PX}px`,
          `min-width:${PAGE_WIDTH_PX}px`,
          `min-height:${PAGE_HEIGHT_PX}px`,
          "height:auto",
          "border:0",
          "visibility:hidden",
        ].join(";")
      );
      document.body.appendChild(iframe);

      const win = iframe.contentWindow;
      if (!win) {
        document.body.removeChild(iframe);
        reject(new Error("Unable to create print frame"));
        return;
      }

      let finished = false;
      let printStarted = false;

      const cleanup = () => {
        if (finished) return;
        finished = true;
        setTimeout(() => {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          resolve();
        }, 300);
      };

      const triggerPrint = () => {
        if (printStarted) return;
        printStarted = true;
        try {
          lockPrintDocumentLayout(win.document, iframe);
          win.focus();
          win.print();
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      win.onafterprint = cleanup;

      const doc = win.document;
      doc.open();
      doc.write(html);
      doc.close();

      const waitForAssets = () => {
        if (printStarted || finished) return;
        lockPrintDocumentLayout(doc, iframe);

        const logo = doc.querySelector("img");
        const tasks = [];

        if (doc.fonts?.ready) {
          tasks.push(doc.fonts.ready.catch(() => {}));
        }

        if (logo && !logo.complete) {
          tasks.push(
            new Promise((res) => {
              logo.addEventListener("load", res, { once: true });
              logo.addEventListener("error", res, { once: true });
            })
          );
        }

        const startPrint = () => {
          lockPrintDocumentLayout(doc, iframe);
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(triggerPrint);
            });
          }, 200);
        };

        if (tasks.length) {
          Promise.all(tasks).then(startPrint);
        } else {
          startPrint();
        }
      };

      if (doc.readyState === "complete") {
        waitForAssets();
      } else {
        iframe.onload = waitForAssets;
      }

      setTimeout(() => {
        if (!printStarted && !finished) {
          finished = true;
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          reject(new Error("Print did not start"));
        }
      }, 15000);

      setTimeout(() => {
        if (printStarted && !finished) cleanup();
      }, 120000);
    } catch (err) {
      reject(err);
    }
  });
}

export default function PrintView() {
  return null;
}
